import React, { useState, useEffect, useRef } from 'react';
import { Heart, Play, Pause, Square, Volume2, Settings, TrendingUp, Maximize2, Trash2 } from 'lucide-react';
import { playAlarmSound, playTestSound, unlockAudio } from '../utils/audioUtils';
import { MEDITATION_MELODIES } from '../constants/meditationMelodies';
import { useFullscreen } from '../contexts/FullscreenContext';
import { saveToStorage, loadFromStorage, STORAGE_KEYS } from '../utils/localStorage';

export default function MeditationTimer() {
  // Browser fullscreen functionality
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  
  // Timer states
  const [duration, setDuration] = useState(600); // 10 minutes default
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('setup'); // setup, preparing, meditating, completed
  
  // Custom duration states
  // eslint-disable-next-line no-unused-vars
  const [showCustomInput, setShowCustomInput] = useState(true);
  const [customMinutes, setCustomMinutes] = useState('');
  const [customSeconds, setCustomSeconds] = useState('');
  
  // Sound settings
  const [showSettings, setShowSettings] = useState(true);
  const [soundSettings, setSoundSettings] = useState({
    startBell: { melody: 'BELL_MEDITATION', volume: 70, enabled: true },
    endBell: { melody: 'BELL_MEDITATION', volume: 70, enabled: true },
    intervalBell: { melody: 'GENTLE_MEDITATION', volume: 60, enabled: false, interval: 300 },
    ambient: { melody: 'NATURE_MEDITATION', volume: 40, enabled: false }
  });

  // Background image settings
  const [selectedBackground, setSelectedBackground] = useState('none');
  const backgroundImages = [
    { id: 'none', name: 'No Background', path: null },
    { id: 'blue-dusk', name: 'Blue Dusk', path: '/images/Blue-Dusk.png' },
    { id: 'deep-forest', name: 'Deep Forest at Dawn', path: '/images/Deep-Forest-at-Dawn.png' },
    { id: 'garden', name: 'Garden Landscape', path: '/images/Garden-Landscape.png' },
    { id: 'navy-blue', name: 'Navy Royal Blue', path: '/images/Navy-Royal-Blue.png' }
  ];

  // Stats
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalMinutes: 0,
    currentStreak: 0,
    lastSessionDate: null
  });

  const intervalRef = useRef(null);
  const preparationTimer = useRef(null);

  // Preset durations
  const presets = [
    { label: '5 min', seconds: 300 },
    { label: '10 min', seconds: 600 },
    { label: '15 min', seconds: 900 },
    { label: '20 min', seconds: 1200 },
    { label: '30 min', seconds: 1800 },
    { label: '45 min', seconds: 2700 },
    { label: '60 min', seconds: 3600 }
  ];

  // Available melodies for meditation
  const meditationMelodies = MEDITATION_MELODIES;

  // Load stats from localStorage
  useEffect(() => {
    const savedStats = loadFromStorage(STORAGE_KEYS.MEDITATION_STATS, {
      totalSessions: 0,
      totalMinutes: 0,
      currentStreak: 0,
      lastSessionDate: null
    });
    
    setStats(savedStats);
    
    // Check if it's a new day to reset streak
    const today = new Date().toDateString();
    const lastSession = savedStats.lastSessionDate;
    if (lastSession && new Date(lastSession).toDateString() !== today) {
      const daysDiff = Math.floor((new Date() - new Date(lastSession)) / (1000 * 60 * 60 * 24));
      if (daysDiff > 1) {
        const updatedStats = { ...savedStats, currentStreak: 0 };
        setStats(updatedStats);
        saveToStorage(STORAGE_KEYS.MEDITATION_STATS, updatedStats);
      }
    }

    const savedSettings = loadFromStorage(STORAGE_KEYS.MEDITATION_SETTINGS, {
      startBell: { melody: 'BELL_MEDITATION', volume: 70, enabled: true },
      endBell: { melody: 'BELL_MEDITATION', volume: 70, enabled: true },
      intervalBell: { melody: 'GENTLE_MEDITATION', volume: 60, enabled: false, interval: 300 },
      ambient: { melody: 'NATURE_MEDITATION', volume: 40, enabled: false }
    });
    setSoundSettings(savedSettings);

    const savedBackground = loadFromStorage(STORAGE_KEYS.MEDITATION_BACKGROUND, 'none');
    setSelectedBackground(savedBackground);
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.MEDITATION_SETTINGS, soundSettings);
  }, [soundSettings]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.MEDITATION_BACKGROUND, selectedBackground);
  }, [selectedBackground]);

  // Timer countdown
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            completeMeditation();
            return 0;
          }
          
          // Check for interval bell
          if (soundSettings.intervalBell.enabled && 
              prev % soundSettings.intervalBell.interval === 0 && 
              prev !== duration) {
            playIntervalBell();
          }
          
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, timeLeft, soundSettings.intervalBell, duration]);

  const playStartBell = async () => {
    if (soundSettings.startBell.enabled) {
      try {
        await unlockAudio();
        await playAlarmSound(soundSettings.startBell.melody, 0.7, false, false); // 不循环播放
      } catch (error) {
        console.error('Error playing start bell:', error);
      }
    }
  };

  const playEndBell = async () => {
    if (soundSettings.endBell.enabled) {
      try {
        await unlockAudio();
        await playAlarmSound(soundSettings.endBell.melody, 0.7, false, false); // 不循环播放
      } catch (error) {
        console.error('Error playing end bell:', error);
      }
    }
  };

  const playIntervalBell = async () => {
    if (soundSettings.intervalBell.enabled) {
      try {
        await unlockAudio();
        await playAlarmSound(soundSettings.intervalBell.melody, 0.6, false, false); // 不循环播放
      } catch (error) {
        console.error('Error playing interval bell:', error);
      }
    }
  };

  const testMelody = async (melodyId) => {
    try {
      await unlockAudio();
      await playTestSound(melodyId);
    } catch (error) {
      console.error('Error testing melody:', error);
    }
  };

  // Custom duration handlers
  const handleCustomDuration = () => {
    const minutes = parseInt(customMinutes) || 0;
    const seconds = parseInt(customSeconds) || 0;
    const totalSeconds = minutes * 60 + seconds;
    
    if (totalSeconds > 0 && totalSeconds <= 7200) { // Max 2 hours
      setDuration(totalSeconds);
      setShowCustomInput(false);
      // 保留用户输入的时长，不清除输入框
    }
  };

  const startMeditation = () => {
    setPhase('preparing');
    setTimeLeft(duration);
    
    // 3 second preparation
    let countdown = 3;
    preparationTimer.current = setInterval(() => {
      countdown--;
      if (countdown <= 0) {
        clearInterval(preparationTimer.current);
        playStartBell();
        setPhase('meditating');
        setIsRunning(true);
        setIsPaused(false);
      }
    }, 1000);
  };

  const pauseMeditation = () => {
    setIsRunning(false);
    setIsPaused(true);
  };

  const resumeMeditation = () => {
    setIsRunning(true);
    setIsPaused(false);
  };

  const stopMeditation = () => {
    setIsRunning(false);
    setIsPaused(false);
    setPhase('setup');
    setTimeLeft(0);
    if (preparationTimer.current) {
      clearInterval(preparationTimer.current);
    }
  };

  const completeMeditation = () => {
    setIsRunning(false);
    setPhase('completed');
    playEndBell();
    
    // Update stats
    const today = new Date().toDateString();
    const isNewDay = !stats.lastSessionDate || new Date(stats.lastSessionDate).toDateString() !== today;
    
    const newStats = {
      totalSessions: stats.totalSessions + 1,
      totalMinutes: stats.totalMinutes + Math.floor(duration / 60),
      currentStreak: isNewDay ? 1 : stats.currentStreak + 1,
      lastSessionDate: new Date().toISOString()
    };
    setStats(newStats);
    saveToStorage(STORAGE_KEYS.MEDITATION_STATS, newStats);
  };

  const clearStats = () => {
    const resetStats = {
      totalSessions: 0,
      totalMinutes: 0,
      currentStreak: 0,
      lastSessionDate: null
    };
    setStats(resetStats);
    saveToStorage(STORAGE_KEYS.MEDITATION_STATS, resetStats);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getBackgroundStyle = () => {
    const selectedBg = backgroundImages.find(bg => bg.id === selectedBackground);
    if (selectedBg && selectedBg.path) {
      return {
        backgroundImage: `url(${selectedBg.path})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    return {};
  };

  const progress = duration > 0 ? ((duration - timeLeft) / duration) * 100 : 0;

  if (phase === 'setup') {
    return (
      <div className="space-y-6">
        {/* Stats Card */}
        <div className="bg-cyan-600/40 backdrop-blur-md rounded-3xl p-6 border border-cyan-400/30 shadow-2xl relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-white" />
              <h3 className="text-xl font-bold text-white">Meditation Progress</h3>
            </div>
            <button
              onClick={clearStats}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
              title="Clear all statistics"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{stats.totalSessions}</div>
              <div className="text-cyan-200 text-sm">Total Sessions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{stats.totalMinutes}</div>
              <div className="text-cyan-200 text-sm">Total Minutes</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white flex items-center justify-center gap-1">
                {stats.currentStreak}
                {stats.currentStreak > 0 && <Heart className="w-6 h-6 text-white" />}
              </div>
              <div className="text-cyan-200 text-sm">Day Streak</div>
            </div>
          </div>
        </div>

        {/* Duration Selection */}
        <div className="bg-cyan-600/40 backdrop-blur-md rounded-3xl p-8 border border-cyan-400/30 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 text-white" />
              <h3 className="text-2xl font-bold text-white">Select Duration</h3>
            </div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Settings className="w-6 h-6 text-white" />
            </button>
          </div>

          <div className="grid grid-cols-4 md:grid-cols-7 gap-3 mb-6">
            {presets.map(preset => (
              <button
                key={preset.seconds}
                onClick={() => setDuration(preset.seconds)}
                className={`p-4 rounded-xl transition-all ${
                  duration === preset.seconds
                    ? 'bg-gradient-to-br from-cyan-500 to-blue-500 text-white scale-105 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <div className="text-sm font-semibold">{preset.label}</div>
              </button>
            ))}
          </div>

          {/* Custom Duration Input */}
          <div className="bg-white/10 rounded-2xl p-4 sm:p-6 mb-6">
              <h4 className="text-white font-semibold text-base sm:text-lg mb-3 sm:mb-4">Custom Duration</h4>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    max="120"
                    value={customMinutes}
                    onChange={(e) => setCustomMinutes(e.target.value)}
                    className="w-14 sm:w-20 px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base bg-white/20 text-white rounded-lg placeholder-white/60 border border-white/30 focus:border-cyan-400 focus:outline-none"
                  />
                  <span className="text-white/90 text-sm sm:text-base">min</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="0"
                    min="0"
                    max="59"
                    value={customSeconds}
                    onChange={(e) => setCustomSeconds(e.target.value)}
                    className="w-14 sm:w-20 px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base bg-white/20 text-white rounded-lg placeholder-white/60 border border-white/30 focus:border-cyan-400 focus:outline-none"
                  />
                  <span className="text-white/90 text-sm sm:text-base">sec</span>
                </div>
                <button
                  onClick={handleCustomDuration}
                  disabled={!customMinutes && !customSeconds}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white rounded-lg transition-colors whitespace-nowrap"
                >
                  Set
                </button>
                <button
                  onClick={() => {
                    setCustomMinutes('');
                    setCustomSeconds('');
                  }}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors whitespace-nowrap"
                >
                  Clear
                </button>
              </div>
            </div>

          {/* Sound Settings Panel */}
          {showSettings && (
            <div className="bg-white/10 rounded-2xl p-6 mb-6 space-y-4">
              <h4 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
                <Volume2 className="w-5 h-5" />
                Sound Settings
              </h4>

              {/* Start Bell */}
              <div>
                <label className="block text-white/90 font-medium mb-2">
                  🔔 Start Bell
                </label>
                <div className="flex gap-3 items-center">
                  <input
                    type="checkbox"
                    checked={soundSettings.startBell.enabled}
                    onChange={(e) => setSoundSettings({
                      ...soundSettings,
                      startBell: { ...soundSettings.startBell, enabled: e.target.checked }
                    })}
                    className="w-5 h-5"
                  />
                  <select
                    value={soundSettings.startBell.melody}
                    onChange={(e) => setSoundSettings({
                      ...soundSettings,
                      startBell: { ...soundSettings.startBell, melody: e.target.value }
                    })}
                    disabled={!soundSettings.startBell.enabled}
                    className="flex-1 px-4 py-2 bg-blue-900 text-white border border-white/30 rounded-lg disabled:opacity-50 focus:border-cyan-400 focus:outline-none"
                  >
                    {meditationMelodies.map(melody => (
                      <option key={melody.id} value={melody.id}>{melody.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => testMelody(soundSettings.startBell.melody)}
                    disabled={!soundSettings.startBell.enabled}
                    className="p-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-500 rounded-lg"
                  >
                    <Play className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>

              {/* End Bell */}
              <div>
                <label className="block text-white/90 font-medium mb-2">
                  🔔 End Bell
                </label>
                <div className="flex gap-3 items-center">
                  <input
                    type="checkbox"
                    checked={soundSettings.endBell.enabled}
                    onChange={(e) => setSoundSettings({
                      ...soundSettings,
                      endBell: { ...soundSettings.endBell, enabled: e.target.checked }
                    })}
                    className="w-5 h-5"
                  />
                  <select
                    value={soundSettings.endBell.melody}
                    onChange={(e) => setSoundSettings({
                      ...soundSettings,
                      endBell: { ...soundSettings.endBell, melody: e.target.value }
                    })}
                    disabled={!soundSettings.endBell.enabled}
                    className="flex-1 px-4 py-2 bg-blue-900 text-white border border-white/30 rounded-lg disabled:opacity-50 focus:border-cyan-400 focus:outline-none"
                  >
                    {meditationMelodies.map(melody => (
                      <option key={melody.id} value={melody.id}>{melody.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => testMelody(soundSettings.endBell.melody)}
                    disabled={!soundSettings.endBell.enabled}
                    className="p-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-500 rounded-lg"
                  >
                    <Play className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>

              {/* Interval Bell */}
              <div>
                <label className="block text-white/90 font-medium mb-2">
                  ⏱️ Interval Reminder
                </label>
                <div className="flex gap-3 items-center mb-2">
                  <input
                    type="checkbox"
                    checked={soundSettings.intervalBell.enabled}
                    onChange={(e) => setSoundSettings({
                      ...soundSettings,
                      intervalBell: { ...soundSettings.intervalBell, enabled: e.target.checked }
                    })}
                    className="w-5 h-5"
                  />
                  <span className="text-white/80">Enable interval reminder</span>
                </div>
                {soundSettings.intervalBell.enabled && (
                  <>
                    <div className="flex gap-3 items-center mb-2">
                      <span className="text-white/80">Every</span>
                      <select
                        value={soundSettings.intervalBell.interval}
                        onChange={(e) => setSoundSettings({
                          ...soundSettings,
                          intervalBell: { ...soundSettings.intervalBell, interval: Number(e.target.value) }
                        })}
                        className="px-4 py-2 bg-blue-900 text-white border border-white/30 rounded-lg focus:border-cyan-400 focus:outline-none"
                      >
                        <option value={300}>5 minutes</option>
                        <option value={600}>10 minutes</option>
                        <option value={900}>15 minutes</option>
                      </select>
                      <select
                        value={soundSettings.intervalBell.melody}
                        onChange={(e) => setSoundSettings({
                          ...soundSettings,
                          intervalBell: { ...soundSettings.intervalBell, melody: e.target.value }
                        })}
                        className="flex-1 px-4 py-2 bg-blue-900 text-white border border-white/30 rounded-lg focus:border-cyan-400 focus:outline-none"
                      >
                        {meditationMelodies.map(melody => (
                          <option key={melody.id} value={melody.id}>{melody.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => testMelody(soundSettings.intervalBell.melody)}
                        className="p-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg"
                      >
                        <Play className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Background Image */}
              <div>
                <label className="block text-white/90 font-medium mb-2">
                  🖼️ Background Image
                </label>
                <div className="flex gap-3 items-center">
                  <select
                    value={selectedBackground}
                    onChange={(e) => setSelectedBackground(e.target.value)}
                    className="flex-1 px-4 py-2 bg-blue-900 text-white border border-white/30 rounded-lg focus:border-cyan-400 focus:outline-none"
                  >
                    {backgroundImages.map(bg => (
                      <option key={bg.id} value={bg.id}>{bg.name}</option>
                    ))}
                  </select>
                  {selectedBackground !== 'none' && (
                     <div className="text-white/70 text-sm">
                       Preview will show during meditation
                     </div>
                   )}
                </div>
              </div>
            </div>
          )}

          {/* Current Duration Display */}
          <div className="text-center mb-6">
            <div className="text-white/80 text-sm mb-2">Selected Duration</div>
            <div className="text-3xl font-bold text-white">
              {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
            </div>
          </div>

          <button
            onClick={startMeditation}
            className="w-full py-5 bg-blue-800 hover:bg-blue-600 text-white rounded-2xl font-bold text-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
          >
            <Play className="w-7 h-7" />
            Start Meditation
          </button>
        </div>

      </div>
    );
  }

  if (phase === 'preparing') {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="bg-cyan-600/40 backdrop-blur-md rounded-3xl p-12 border border-cyan-400/30 shadow-2xl text-center max-w-lg">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/20 flex items-center justify-center">
              <Heart className="w-12 h-12 text-white animate-pulse" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Getting Ready</h2>
            <p className="text-cyan-100 text-lg">
              Find a comfortable position and prepare to begin your meditation journey...
            </p>
          </div>
          <button
            onClick={stopMeditation}
            className="px-8 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'meditating') {
    return (
      <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'fixed top-20 left-0 right-0 h-[calc(100vh-5rem)] z-20'} flex items-center justify-center`}>
        {/* Desktop: 16:9 Ratio Layout - 1920px width, full height */}
        <div 
          className={`hidden lg:flex flex-col bg-gradient-to-br from-indigo-600/40 to-purple-600/40 backdrop-blur-md border border-indigo-400/30 shadow-2xl p-8 relative ${isFullscreen ? 'w-full h-full' : 'w-[1920px] h-full'}`}
          style={getBackgroundStyle()}
        >
          {/* Fullscreen Button */}
          <button
            onClick={toggleFullscreen}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors z-[60]"
          >
            <Maximize2 className="w-6 h-6 text-gray-700" />
          </button>
          {/* Center - Progress Circle */}
          <div className="flex-1 flex items-center justify-center">
            <div className="relative w-80 h-80">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={`${progress * 2.827} 282.7`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-6xl font-bold text-white mb-2">
                  {formatTime(timeLeft)}
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom - Controls */}
          <div className="flex justify-center gap-4 pb-4">
            {isPaused ? (
              <button
                onClick={resumeMeditation}
                className="p-3 bg-white/30 hover:bg-white/40 backdrop-blur-md text-white rounded-xl transition-all shadow-xl border-2 border-white/50 hover:border-white/70 hover:scale-105"
              >
                <Play className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={pauseMeditation}
                className="p-3 bg-white/30 hover:bg-white/40 backdrop-blur-md text-white rounded-xl transition-all shadow-xl border-2 border-white/50 hover:border-white/70 hover:scale-105"
              >
                <Pause className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={stopMeditation}
              className="p-3 bg-white/30 hover:bg-white/40 backdrop-blur-md text-white rounded-xl transition-all shadow-xl border-2 border-white/50 hover:border-white/70 hover:scale-105"
            >
              <Square className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mobile: 9:16 Portrait Layout */}
        <div 
          className={`lg:hidden bg-gradient-to-br from-indigo-600/40 to-purple-600/40 backdrop-blur-md border border-indigo-400/30 shadow-2xl text-center flex flex-col justify-center p-6 relative ${isFullscreen ? 'w-full h-full' : 'rounded-3xl w-full max-w-sm mx-4 min-h-[80vh]'}`}
          style={getBackgroundStyle()}
        >
          {/* Fullscreen Button */}
          <button
            onClick={toggleFullscreen}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors z-[60]"
          >
            <Maximize2 className="w-5 h-5 text-white" />
          </button>
          {/* Header */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-2">Meditation in Progress</h3>
            <p className="text-white/80 text-sm">Stay focused and breathe naturally</p>
          </div>

          {/* Progress Circle */}
          <div className="flex-1 flex items-center justify-center mb-8">
            <div className="relative w-72 h-72">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={`${progress * 2.827} 282.7`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-4xl sm:text-5xl font-bold text-white mb-2">
                  {formatTime(timeLeft)}
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex justify-center gap-4 mb-4">
            {isPaused ? (
              <button
                onClick={resumeMeditation}
                className="p-3 bg-white/30 hover:bg-white/40 backdrop-blur-md text-white rounded-xl transition-all shadow-xl border-2 border-white/50 hover:border-white/70 hover:scale-105"
              >
                <Play className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={pauseMeditation}
                className="p-3 bg-white/30 hover:bg-white/40 backdrop-blur-md text-white rounded-xl transition-all shadow-xl border-2 border-white/50 hover:border-white/70 hover:scale-105"
              >
                <Pause className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={stopMeditation}
              className="p-3 bg-white/30 hover:bg-white/40 backdrop-blur-md text-white rounded-xl transition-all shadow-xl border-2 border-white/50 hover:border-white/70 hover:scale-105"
            >
              <Square className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'completed') {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="bg-gradient-to-br from-green-600/40 to-emerald-600/40 backdrop-blur-md rounded-3xl p-12 border border-green-400/30 shadow-2xl text-center max-w-lg">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/20 flex items-center justify-center">
              <Heart className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Meditation Complete!</h2>
            <p className="text-green-100 text-lg mb-6">
              Congratulations! You completed a {Math.floor(duration / 60)} minute meditation session
            </p>
            
            {/* Session Stats */}
            <div className="bg-white/10 rounded-2xl p-6 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{stats.totalSessions}</div>
                  <div className="text-green-200 text-sm">Total Sessions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white flex items-center justify-center gap-1">
                    {stats.currentStreak}
                    {stats.currentStreak > 0 && <Heart className="w-5 h-5 text-red-500" />}
                  </div>
                  <div className="text-green-200 text-sm">Day Streak</div>
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => {
              setPhase('setup');
              setTimeLeft(0);
            }}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-2xl font-bold text-lg transition-all shadow-lg hover:shadow-xl"
          >
            Start New Meditation
          </button>
        </div>
      </div>
    );
  }

  return null;
}